import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  CRITICALITY_LABELS_UK,
  DEVICE_TYPE_LABELS_UK,
  PORT_LAYOUT_OPTIONS,
  ETHERNET_PORT_ID,
  defaultDeviceInterfaces,
  defaultPortLayout,
  deviceSupportsPorts,
  deviceSupportsWifiAntennas,
  displayPortId,
  interfacesWiredCount,
  isWifiPortId,
  listNodePorts,
  occupiedPortsForNode,
  interfacesForEditor,
  resolvePortCount,
  resolvePortLayout,
  resolveWifiAntennaCount,
  summarizeInterfacesUk,
  type ConnectionType,
  type Criticality,
  type DeviceInterfaces,
  type DeviceInventory,
  type DeviceType,
  type DiagramEdge,
  type DiagramNode,
  type MonitorMode,
  type MonitorSource,
  type PortLayout,
} from '@ukrop-nm/shared';
import { DeviceLiveLook } from '../device/DeviceLiveLook';
import {
  normalizeMac,
  refreshAppearanceFromNameModel,
  resolveDeviceAppearance,
  resolveOuiVendor,
} from '../device/deviceAppearance';
import { ColorSwatchPicker } from './ColorSwatchPicker';
import { UkropTip } from '../tooltips/DiagramTooltipContext';

const MONITOR_MODE_LABELS_UK: Record<MonitorMode, string> = {
  passive: 'Пасивний',
  icmp: 'ICMP (пінг)',
  tcp: 'TCP',
  http: 'HTTP',
  dhcp_presence: 'DHCP присутність',
  arp_presence: 'ARP присутність',
  wifi_registration: 'Реєстрація Wi‑Fi',
  router_interface: 'Інтерфейс маршрутизатора',
  neighbor: 'Сусід (neighbor)',
  derived: 'Виведений',
  manual: 'Вручну',
};

const MONITOR_MODES = Object.keys(MONITOR_MODE_LABELS_UK) as MonitorMode[];

const CONNECTION_TYPES: ConnectionType[] = [
  'ethernet',
  'fiber',
  'wifi',
  'wan',
  'sip',
  'logical',
  'unknown',
];

const CONNECTION_TYPE_LABELS_UK: Record<ConnectionType, string> = {
  ethernet: 'Ethernet',
  fiber: 'Оптоволокно',
  wifi: 'Wi‑Fi',
  wan: 'WAN',
  sip: 'SIP / телефонія',
  logical: 'Логічне',
  unknown: 'Невідомо',
};

const MONITOR_SOURCE_LABELS_UK: Record<MonitorSource, string> = {
  interface: 'Інтерфейс',
  neighbor: 'Сусід',
  'endpoint-derived': 'З кінцевих точок',
  manual: 'Вручну',
  unknown: 'Невідомо',
};

const MONITOR_SOURCES = Object.keys(MONITOR_SOURCE_LABELS_UK) as MonitorSource[];

function IosSwitch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`ios-switch${checked ? ' is-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="ios-switch-track">
        <span className="ios-switch-thumb" />
      </span>
      <span className="ios-switch-label">{label}</span>
    </button>
  );
}

function syncLegacyFromInterfaces(iface: DeviceInterfaces): {
  portCount: number;
  wifiAntennaCount: number;
} {
  return {
    portCount: interfacesWiredCount(iface),
    wifiAntennaCount: iface.hasWifi ? Math.max(0, iface.wifiCount ?? 0) : 0,
  };
}

function InventoryPreview({
  inventory,
  type,
  label,
}: {
  inventory: DeviceInventory;
  type: DeviceType;
  label: string;
}) {
  const appearance = resolveDeviceAppearance({
    label,
    model: inventory.model,
    vendor: inventory.vendor || resolveOuiVendor(inventory.macLocal),
    macLocal: inventory.macLocal,
    type,
    hasPhoto: Boolean(inventory.photoDataUrl),
    appearanceId: inventory.appearanceId,
  });

  return (
    <div className="edit-look-preview">
      {inventory.photoDataUrl ? (
        <img src={inventory.photoDataUrl} alt="Фото пристрою" className="info-look-photo" />
      ) : (
        <DeviceLiveLook
          key={appearance.id}
          appearanceId={appearance.id}
          className="info-look-svg"
        />
      )}
      <div className="text-theme-xs text-gray-500 dark:text-gray-400">
        Превʼю: <strong className="text-gray-700 dark:text-gray-200">{appearance.title}</strong> ·{' '}
        {appearance.subtitle}
      </div>
    </div>
  );
}

export function NodeEditModal({
  node,
  disabled,
  onSave,
  onClose,
}: {
  node: DiagramNode;
  disabled: boolean;
  onSave: (patch: Partial<DiagramNode>) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(node);
  const dirtyRef = useRef(false);

  useEffect(() => {
    dirtyRef.current = false;
    setDraft(node);
  }, [node.id]);

  useEffect(() => {
    if (dirtyRef.current) return;
    setDraft(node);
  }, [node]);

  const set = <K extends keyof DiagramNode>(key: K, value: DiagramNode[K]) => {
    dirtyRef.current = true;
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const inventory: DeviceInventory = draft.inventory ?? {};
  const setInv = <K extends keyof DeviceInventory>(key: K, value: DeviceInventory[K]) => {
    dirtyRef.current = true;
    setDraft((prev) => ({
      ...prev,
      inventory: { ...(prev.inventory ?? {}), [key]: value },
    }));
  };

  const ifaceDraft = useMemo(() => interfacesForEditor(draft), [draft]);

  const patchIface = (patch: Partial<DeviceInterfaces>) => {
    dirtyRef.current = true;
    setDraft((prev) => {
      const base = interfacesForEditor(prev);
      const interfaces = { ...base, ...patch };
      const legacy = syncLegacyFromInterfaces(interfaces);
      return {
        ...prev,
        interfaces,
        portProfileId: undefined,
        portCount: legacy.portCount,
        wifiAntennaCount: legacy.wifiAntennaCount || undefined,
      };
    });
  };

  const onPhoto = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 1_500_000) {
      window.alert('Фото завелике (макс. ~1.5 МБ). Стисніть зображення.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        dirtyRef.current = true;
        setDraft((prev) => ({
          ...prev,
          inventory: {
            ...(prev.inventory ?? {}),
            photoDataUrl: reader.result as string,
            appearanceId: undefined,
          },
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const refreshLook = () => {
    const next = refreshAppearanceFromNameModel({
      label: draft.label,
      model: inventory.model,
      vendor: inventory.vendor || resolveOuiVendor(inventory.macLocal),
      macLocal: inventory.macLocal,
      type: draft.type,
    });
    const isStarlink = next.id === 'starlink';
    dirtyRef.current = true;
    setDraft((prev) => ({
      ...prev,
      type: isStarlink ? 'satellite' : prev.type,
      inventory: {
        ...(prev.inventory ?? {}),
        photoDataUrl: undefined,
        appearanceId: next.id,
        vendor:
          isStarlink && !prev.inventory?.vendor?.trim()
            ? 'Starlink'
            : prev.inventory?.vendor,
        model:
          prev.inventory?.model?.trim() ||
          (isStarlink ? prev.label : prev.inventory?.model),
      },
    }));
  };

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-labelledby="node-edit-title">
      <button type="button" className="modal-backdrop" aria-label="Закрити" onClick={onClose} />
      <div className="modal-panel modal-panel-wide">
        <div className="modal-header">
          <h3 id="node-edit-title">
            {draft.type === 'zone' ? 'Редагування зони' : 'Редагування пристрою'}
          </h3>
          <button type="button" className="btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <section className="edit-section">
            <h4 className="edit-section-title">Основне</h4>
            <div className="field">
              <label>Назва</label>
              <input
                disabled={disabled}
                value={draft.label}
                onChange={(e) => set('label', e.target.value)}
              />
            </div>
            <div className="field">
              <label>Тип</label>
              <select
                disabled={disabled}
                value={draft.type}
                onChange={(e) => {
                  const type = e.target.value as DeviceType;
                  setDraft((prev) => {
                    const interfaces =
                      deviceSupportsPorts(type) || deviceSupportsWifiAntennas(type)
                        ? defaultDeviceInterfaces(type)
                        : undefined;
                    const legacy = interfaces
                      ? syncLegacyFromInterfaces(interfaces)
                      : {
                          portCount: undefined as number | undefined,
                          wifiAntennaCount: undefined as number | undefined,
                        };
                    return {
                      ...prev,
                      type,
                      interfaces,
                      portProfileId: undefined,
                      portCount: legacy.portCount,
                      portLayout: deviceSupportsPorts(type)
                        ? prev.portLayout ?? defaultPortLayout(type)
                        : undefined,
                      wifiAntennaCount: legacy.wifiAntennaCount,
                    };
                  });
                }}
              >
                {(Object.keys(DEVICE_TYPE_LABELS_UK) as DeviceType[]).map((t) => (
                  <option key={t} value={t}>
                    {DEVICE_TYPE_LABELS_UK[t]}
                  </option>
                ))}
              </select>
            </div>
            {draft.type === 'zone' ? (
              <ColorSwatchPicker
                label="Колір зони"
                disabled={disabled}
                value={draft.color}
                onChange={(hex) => set('color', hex)}
              />
            ) : (
              <>
                <div className="field">
                  <label>Виробник</label>
                  <input
                    disabled={disabled}
                    value={inventory.vendor ?? ''}
                    placeholder="авто з MAC OUI, якщо порожньо"
                    onChange={(e) => setInv('vendor', e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Модель</label>
                  <input
                    disabled={disabled}
                    value={inventory.model ?? ''}
                    placeholder="напр. RB2011UiAS, CRS326-24G-2S+"
                    onChange={(e) => setInv('model', e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>IP Address</label>
                  <input
                    disabled={disabled}
                    value={inventory.ipLocal ?? ''}
                    placeholder="напр. 192.168.88.1"
                    onChange={(e) => setInv('ipLocal', e.target.value)}
                  />
                  <span className="text-theme-xs text-gray-500">
                    Локально в схемі, не потрапляє в cloud telemetry.
                  </span>
                </div>
                <div className="field">
                  <label>MAC Address</label>
                  <input
                    disabled={disabled}
                    value={inventory.macLocal ?? ''}
                    placeholder="AA:BB:CC:DD:EE:FF"
                    onChange={(e) => setInv('macLocal', e.target.value)}
                    onBlur={() => {
                      const n = normalizeMac(inventory.macLocal);
                      if (n) setInv('macLocal', n);
                    }}
                  />
                  {resolveOuiVendor(inventory.macLocal) ? (
                    <span className="text-theme-xs text-gray-500">
                      OUI: {resolveOuiVendor(inventory.macLocal)}
                    </span>
                  ) : null}
                </div>
              </>
            )}
          </section>

          <section className="edit-section">
            <h4 className="edit-section-title">Моніторинг і важливість</h4>
            <div className="field">
              <label>Критичність</label>
              <select
                disabled={disabled}
                value={draft.criticality}
                onChange={(e) => set('criticality', e.target.value as Criticality)}
              >
                {(Object.keys(CRITICALITY_LABELS_UK) as Criticality[]).map((c) => (
                  <option key={c} value={c}>
                    {CRITICALITY_LABELS_UK[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Ключ моніторингу</label>
              <input
                disabled={disabled || draft.type === 'zone'}
                value={draft.monitorKey}
                onChange={(e) => set('monitorKey', e.target.value)}
                placeholder="унікальний ключ для телеметрії"
              />
            </div>
            <div className="field">
              <label>Режим моніторингу</label>
              <select
                disabled={disabled}
                value={draft.monitorMode}
                onChange={(e) => set('monitorMode', e.target.value as MonitorMode)}
              >
                {MONITOR_MODES.map((m) => (
                  <option key={m} value={m}>
                    {MONITOR_MODE_LABELS_UK[m]}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {deviceSupportsPorts(draft.type) || deviceSupportsWifiAntennas(draft.type) ? (
            <section className="edit-section">
              <h4 className="edit-section-title">Інтерфейси</h4>
              <fieldset className="iface-fieldset">
                <p className="iface-hint">
                  Універсальні перемикачі: налаштуйте порти як у реальному приладі або в інструкції
                  (напр. RB2011UiAS-RM: WAN + 9 Eth + 1 SFP).
                </p>
                <div className="iface-summary">{summarizeInterfacesUk(ifaceDraft)}</div>

                {deviceSupportsPorts(draft.type) ? (
                  <>
                    <div className="iface-row">
                      <IosSwitch
                        label="WAN (1 порт RJ45)"
                        checked={Boolean(ifaceDraft.hasWan)}
                        disabled={disabled}
                        onChange={(hasWan) => patchIface({ hasWan })}
                      />
                    </div>
                    <div className="iface-row iface-row-split">
                      <IosSwitch
                        label="Ethernet / LAN"
                        checked={Boolean(ifaceDraft.hasEthernet)}
                        disabled={disabled}
                        onChange={(hasEthernet) =>
                          patchIface({
                            hasEthernet,
                            ethernetCount: hasEthernet
                              ? Math.max(1, ifaceDraft.ethernetCount ?? 4)
                              : 0,
                          })
                        }
                      />
                      {ifaceDraft.hasEthernet ? (
                        <label className="iface-count">
                          Кількість
                          <input
                            type="number"
                            min={0}
                            max={64}
                            disabled={disabled}
                            value={ifaceDraft.ethernetCount ?? 0}
                            onChange={(e) =>
                              patchIface({
                                ethernetCount: Math.max(0, Number(e.target.value) || 0),
                                hasEthernet: true,
                              })
                            }
                          />
                        </label>
                      ) : null}
                    </div>
                    <div className="iface-row iface-row-split">
                      <IosSwitch
                        label="SFP"
                        checked={Boolean(ifaceDraft.hasSfp)}
                        disabled={disabled}
                        onChange={(hasSfp) =>
                          patchIface({
                            hasSfp,
                            sfpCount: hasSfp
                              ? Math.max(1, Math.min(4, ifaceDraft.sfpCount ?? 1))
                              : 0,
                          })
                        }
                      />
                      {ifaceDraft.hasSfp ? (
                        <label className="iface-count">
                          Кількість (0–4)
                          <input
                            type="number"
                            min={0}
                            max={4}
                            disabled={disabled}
                            value={ifaceDraft.sfpCount ?? 0}
                            onChange={(e) =>
                              patchIface({
                                sfpCount: Math.min(4, Math.max(0, Number(e.target.value) || 0)),
                                hasSfp: true,
                              })
                            }
                          />
                        </label>
                      ) : null}
                    </div>
                    <div className="field">
                      <label>Розташування портів Eth/LAN</label>
                      <select
                        disabled={disabled}
                        value={draft.portLayout ?? defaultPortLayout(draft.type)}
                        onChange={(e) => set('portLayout', e.target.value as PortLayout)}
                      >
                        {PORT_LAYOUT_OPTIONS.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.labelUk}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : null}

                {deviceSupportsWifiAntennas(draft.type) ? (
                  <>
                    <div className="iface-row iface-row-split">
                      <IosSwitch
                        label="Wi‑Fi"
                        checked={Boolean(ifaceDraft.hasWifi)}
                        disabled={disabled}
                        onChange={(hasWifi) =>
                          patchIface({
                            hasWifi,
                            wifiCount: hasWifi ? Math.max(1, ifaceDraft.wifiCount ?? 2) : 0,
                            wifi24: hasWifi ? ifaceDraft.wifi24 ?? true : false,
                            wifi58: hasWifi ? ifaceDraft.wifi58 ?? false : false,
                          })
                        }
                      />
                      {ifaceDraft.hasWifi ? (
                        <label className="iface-count">
                          Антени
                          <input
                            type="number"
                            min={0}
                            max={16}
                            disabled={disabled}
                            value={ifaceDraft.wifiCount ?? 0}
                            onChange={(e) =>
                              patchIface({
                                wifiCount: Math.max(0, Number(e.target.value) || 0),
                                hasWifi: true,
                              })
                            }
                          />
                        </label>
                      ) : null}
                    </div>
                    {ifaceDraft.hasWifi ? (
                      <div className="iface-bands">
                        <IosSwitch
                          label="2.4 ГГц"
                          checked={Boolean(ifaceDraft.wifi24)}
                          disabled={disabled}
                          onChange={(wifi24) => patchIface({ wifi24 })}
                        />
                        <IosSwitch
                          label="5.8 ГГц"
                          checked={Boolean(ifaceDraft.wifi58)}
                          disabled={disabled}
                          onChange={(wifi58) => patchIface({ wifi58 })}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}
              </fieldset>
            </section>
          ) : null}

          {draft.type !== 'zone' ? (
            <section className="edit-section">
              <h4 className="edit-section-title">Розташування та вигляд</h4>
              <div className="field">
                <label>Розташування</label>
                <input
                  disabled={disabled}
                  value={inventory.location ?? ''}
                  onChange={(e) => setInv('location', e.target.value)}
                />
              </div>
              <div className="field">
                <label>Фото «як виглядає вживу»</label>
                <div className="flex flex-wrap items-center gap-2">
                  <input type="file" accept="image/*" disabled={disabled} onChange={onPhoto} />
                  <UkropTip text="Підібрати малюнок за назвою та моделлю">
                    <button
                      type="button"
                      className="btn-sm"
                      disabled={disabled}
                      onClick={refreshLook}
                    >
                      Оновити малюнок
                    </button>
                  </UkropTip>
                  {inventory.photoDataUrl ? (
                    <button
                      type="button"
                      className="btn-sm"
                      disabled={disabled}
                      onClick={() => setInv('photoDataUrl', undefined)}
                    >
                      Прибрати фото
                    </button>
                  ) : null}
                </div>
              </div>
              <InventoryPreview inventory={inventory} type={draft.type} label={draft.label} />
            </section>
          ) : null}

          <section className="edit-section">
            <h4 className="edit-section-title">Додатково</h4>
            <label className="mb-3 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                disabled={disabled}
                checked={Boolean(draft.locked)}
                onChange={(e) => set('locked', e.target.checked)}
              />
              Заблоковано
            </label>
            <div className="field">
              <label>Опис</label>
              <textarea
                disabled={disabled}
                rows={2}
                value={draft.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
            <div className="field">
              <label>Нотатки</label>
              <textarea
                disabled={disabled}
                rows={3}
                value={draft.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
              />
            </div>
          </section>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-sm" onClick={onClose}>
            Скасувати
          </button>
          <button
            type="button"
            className="btn-sm bg-brand-500 text-white hover:bg-brand-600"
            disabled={disabled}
            onClick={() => {
              onSave({
                label: draft.label,
                monitorKey: draft.monitorKey,
                type: draft.type,
                criticality: draft.criticality,
                monitorMode: draft.monitorMode,
                locked: draft.locked,
                description: draft.description,
                notes: draft.notes,
                color: draft.color,
                interfaces:
                  deviceSupportsPorts(draft.type) || deviceSupportsWifiAntennas(draft.type)
                    ? ifaceDraft
                    : undefined,
                portCount: deviceSupportsPorts(draft.type)
                  ? resolvePortCount({ ...draft, interfaces: ifaceDraft })
                  : undefined,
                portLayout: deviceSupportsPorts(draft.type)
                  ? resolvePortLayout(draft)
                  : undefined,
                portProfileId: undefined,
                wifiAntennaCount: deviceSupportsWifiAntennas(draft.type)
                  ? resolveWifiAntennaCount({ ...draft, interfaces: ifaceDraft })
                  : undefined,
                inventory: {
                  model: inventory.model?.trim() || undefined,
                  ipLocal: inventory.ipLocal?.trim() || undefined,
                  macLocal:
                    normalizeMac(inventory.macLocal) ??
                    (inventory.macLocal?.trim() || undefined),
                  vendor: inventory.vendor?.trim() || undefined,
                  location: inventory.location?.trim() || undefined,
                  photoDataUrl: inventory.photoDataUrl,
                  appearanceId: inventory.appearanceId,
                },
              });
              onClose();
            }}
          >
            Зберегти
          </button>
        </div>
      </div>
    </div>
  );
}

export function EdgeEditModal({
  edge,
  nodes,
  edges,
  disabled,
  onSave,
  onClose,
}: {
  edge: DiagramEdge;
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  disabled: boolean;
  onSave: (patch: Partial<DiagramEdge>) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(edge);
  const dirtyRef = useRef(false);

  useEffect(() => {
    dirtyRef.current = false;
    setDraft(edge);
  }, [edge.id]);

  useEffect(() => {
    if (dirtyRef.current) return;
    setDraft(edge);
  }, [edge]);

  const set = <K extends keyof DiagramEdge>(key: K, value: DiagramEdge[K]) => {
    dirtyRef.current = true;
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const sourceNode = nodes.find((n) => n.id === draft.sourceNodeId);
  const targetNode = nodes.find((n) => n.id === draft.targetNodeId);
  const sourceBusy = occupiedPortsForNode(edges, draft.sourceNodeId, draft.id);
  const targetBusy = occupiedPortsForNode(edges, draft.targetNodeId, draft.id);
  const sourcePorts = sourceNode ? listNodePorts(sourceNode) : [];
  const targetPorts = targetNode ? listNodePorts(targetNode) : [];

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-labelledby="edge-edit-title">
      <button type="button" className="modal-backdrop" aria-label="Закрити" onClick={onClose} />
      <div className="modal-panel">
        <div className="modal-header">
          <h3 id="edge-edit-title">Редагування зʼєднання</h3>
          <button type="button" className="btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <div className="port-link-summary">
            <div>
              <div className="text-theme-xs text-gray-500">Від</div>
              <strong>{sourceNode?.label ?? draft.sourceNodeId}</strong>
            </div>
            <div className="port-link-arrow" aria-hidden>
              →
            </div>
            <div>
              <div className="text-theme-xs text-gray-500">До</div>
              <strong>{targetNode?.label ?? draft.targetNodeId}</strong>
            </div>
          </div>

          <section className="edit-section">
            <h4 className="edit-section-title">Основне</h4>
            <div className="field">
              <label>Назва</label>
              <input
                disabled={disabled}
                value={draft.label ?? ''}
                onChange={(e) => set('label', e.target.value)}
              />
            </div>
            <div className="field">
              <label>Тип</label>
              <select
                disabled={disabled}
                value={draft.connectionType}
                onChange={(e) => set('connectionType', e.target.value as ConnectionType)}
              >
                {CONNECTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {CONNECTION_TYPE_LABELS_UK[t]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Мережа</label>
              <input
                disabled={disabled}
                value={draft.networkCidr ?? ''}
                placeholder="192.168.1.0/24"
                onChange={(e) => set('networkCidr', e.target.value)}
              />
              <span className="text-theme-xs text-gray-500">
                Мережа і маска через дріб — IP, які можуть іти цим зʼєднанням. Локально в схемі.
              </span>
            </div>
          </section>

          <section className="edit-section">
            <h4 className="edit-section-title">Порти</h4>
            <div className="field">
              <label>Порт джерела ({sourceNode?.label ?? '—'})</label>
              <select
                disabled={disabled || sourcePorts.length === 0}
                value={displayPortId(draft.sourcePort)}
                onChange={(e) => {
                  const sourcePort = e.target.value || ETHERNET_PORT_ID;
                  setDraft((prev) => ({
                    ...prev,
                    sourcePort,
                    connectionType:
                      isWifiPortId(sourcePort) || isWifiPortId(prev.targetPort)
                        ? prev.connectionType === 'ethernet' || !prev.connectionType
                          ? 'wifi'
                          : prev.connectionType
                        : prev.connectionType,
                  }));
                }}
              >
                <option value={ETHERNET_PORT_ID}>{ETHERNET_PORT_ID}</option>
                {sourcePorts.map((p) => {
                  const busy = sourceBusy.has(p.id);
                  return (
                    <option key={p.id} value={p.id} disabled={busy && draft.sourcePort !== p.id}>
                      {p.label}
                      {busy && draft.sourcePort !== p.id ? ' · зайнятий' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="field">
              <label>Порт призначення ({targetNode?.label ?? '—'})</label>
              <select
                disabled={disabled || targetPorts.length === 0}
                value={displayPortId(draft.targetPort)}
                onChange={(e) => {
                  const targetPort = e.target.value || ETHERNET_PORT_ID;
                  setDraft((prev) => ({
                    ...prev,
                    targetPort,
                    connectionType:
                      isWifiPortId(prev.sourcePort) || isWifiPortId(targetPort)
                        ? prev.connectionType === 'ethernet' || !prev.connectionType
                          ? 'wifi'
                          : prev.connectionType
                        : prev.connectionType,
                  }));
                }}
              >
                <option value={ETHERNET_PORT_ID}>{ETHERNET_PORT_ID}</option>
                {targetPorts.map((p) => {
                  const busy = targetBusy.has(p.id);
                  return (
                    <option key={p.id} value={p.id} disabled={busy && draft.targetPort !== p.id}>
                      {p.label}
                      {busy && draft.targetPort !== p.id ? ' · зайнятий' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
          </section>

          <section className="edit-section">
            <h4 className="edit-section-title">Моніторинг і важливість</h4>
            <div className="field">
              <label>Критичність</label>
              <select
                disabled={disabled}
                value={draft.criticality}
                onChange={(e) => set('criticality', e.target.value as Criticality)}
              >
                {(Object.keys(CRITICALITY_LABELS_UK) as Criticality[]).map((c) => (
                  <option key={c} value={c}>
                    {CRITICALITY_LABELS_UK[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Ключ моніторингу</label>
              <input
                disabled={disabled}
                value={draft.monitorKey ?? ''}
                onChange={(e) => set('monitorKey', e.target.value)}
              />
            </div>
            <div className="field">
              <label>Джерело моніторингу</label>
              <select
                disabled={disabled}
                value={draft.monitorSource}
                onChange={(e) => set('monitorSource', e.target.value as MonitorSource)}
              >
                {MONITOR_SOURCES.map((t) => (
                  <option key={t} value={t}>
                    {MONITOR_SOURCE_LABELS_UK[t]}
                  </option>
                ))}
              </select>
            </div>
            <ColorSwatchPicker
              label="Колір зʼєднання"
              disabled={disabled}
              value={draft.color}
              onChange={(hex) => set('color', hex)}
            />
          </section>

          <section className="edit-section">
            <h4 className="edit-section-title">Додатково</h4>
            <div className="field">
              <label>Нотатки</label>
              <textarea
                disabled={disabled}
                rows={3}
                value={draft.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
              />
            </div>
          </section>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-sm" onClick={onClose}>
            Скасувати
          </button>
          <button
            type="button"
            className="btn-sm bg-brand-500 text-white hover:bg-brand-600"
            disabled={disabled}
            onClick={() => {
              onSave({
                label: draft.label,
                connectionType: draft.connectionType,
                monitorSource: draft.monitorSource,
                monitorKey: draft.monitorKey,
                criticality: draft.criticality,
                notes: draft.notes,
                color: draft.color,
                networkCidr: draft.networkCidr?.trim() || undefined,
                sourcePort: displayPortId(draft.sourcePort),
                targetPort: displayPortId(draft.targetPort),
              });
              onClose();
            }}
          >
            Зберегти
          </button>
        </div>
      </div>
    </div>
  );
}
